import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CalendarDays, CalendarRange, Check, Clock, Download, HandCoins, HelpCircle, IndianRupee, Receipt, TrendingUp, Users, Wallet, X } from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import { useSmartBack } from '../../../shared/navigation';
import Loader from '../../../shared/components/ui/Loader';
import { useProjectsList } from '../../../../contexts/ProjectsContext';
import {
  getDailyAttendanceReport,
  getMonthlyAttendanceReport,
  getMonthlyStaffAttendanceReport,
  getDailyPayoutReport,
  getMonthlyPayReport,
  downloadWorkforceReport
} from '../../services/api/implementation/impl';
import {
  StatusPill,
  Tile,
  toneForStatus
} from '../../../shared/components/ui/primitives';

/**
 * Attendance and payout reports.
 *
 * These are projections, not a second source of truth. Every figure here comes
 * from the muster or the wage ledger — if a report disagrees with the Wages
 * screen, the report is wrong.
 *
 * Kept apart from the CLRA statutory registers on purpose. Those are legal
 * documents with fixed layouts, filed with an inspector; these are management
 * reports and are free to change shape.
 */

const TABS = [
  { key: 'daily', label: 'Daily attendance', icon: CalendarDays, kind: 'labour' },
  { key: 'monthly', label: 'Monthly muster', icon: CalendarRange, kind: 'labour' },
  { key: 'staff', label: 'Staff attendance', icon: Users, kind: 'employees' },
  { key: 'payouts', label: 'Payouts', icon: Wallet, kind: 'labour' },
  { key: 'pay', label: 'Monthly pay', icon: Wallet, kind: 'labour' }
];

/* Which half of the workforce the reports are for. Reached from Reports →
 * Employee Reports / Labour Reports (29 Sep) as well as from Workforce; the
 * header chips switch between them and the choice stays in the URL. */
const KINDS = [
  { key: 'labour', label: 'Labour', title: 'Labour Reports' },
  { key: 'employees', label: 'Employees', title: 'Employee Reports' }
];

/* Same chip as ManagementHeaderTabs. */
const kindTabClass = (active) =>
  `rounded-md px-4 py-1.5 text-sm font-medium border transition-all duration-200 shrink-0 ${
    active
      ? 'bg-[#2A565E] text-white border-[#2A565E]'
      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
  }`;

const WorkforceReports = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  // Back goes to the Workforce tab the reports belong to, not its dashboard.
  const { onBack } = useSmartBack({
    fallback: `/builder/workforce?tab=${searchParams.get('kind') === 'employees' ? 'employees' : 'labour'}`
  });
  const kind = KINDS.find((k) => k.key === searchParams.get('kind')) ?? null;
  const visibleTabs = kind ? TABS.filter((t) => t.kind === kind.key) : TABS;
  const [chosenTab, setTab] = useState(visibleTabs[0].key);
  // Switching Labour ↔ Employees must not leave a report from the other half
  // selected, so an out-of-range choice falls back to the first one shown.
  const tab = visibleTabs.some((t) => t.key === chosenTab) ? chosenTab : visibleTabs[0].key;
  const selectKind = (key) => setSearchParams({ kind: key });
  const [date, setDate] = useState(todayString);
  const [month, setMonth] = useState(thisMonthString);
  const [projectId, setProjectId] = useState('');
  const [range, setRange] = useState({ from: '', to: '' });
  const { projectsForDropdown } = useProjectsList();

  const download = async (path, params) => {
    try {
      const filename = await downloadWorkforceReport(path, params);
      toast.success(`Downloaded ${filename}`);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not download the report');
    }
  };

  return (
    <Layout>
      <Header backArrow onBack={onBack} data={{ name: kind ? kind.title : 'Workforce Reports' }}>
        <div className="flex items-center gap-2" role="tablist" aria-label="Report for">
          {KINDS.map((k) => (
            <button
              key={k.key}
              type="button"
              role="tab"
              aria-selected={kind?.key === k.key}
              onClick={() => selectKind(k.key)}
              className={kindTabClass(kind?.key === k.key)}>
              {k.label}
            </button>
          ))}
        </div>
      </Header>

      <div className="space-y-4 p-4">
        <div className="flex flex-wrap gap-1 rounded-lg border border-gray-200 bg-white p-2">
          {visibleTabs.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium ${
                tab === key
                  ? 'bg-brand-700 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>

        {tab === 'daily' && (
          <DailyAttendance
            date={date}
            setDate={setDate}
            projectId={projectId}
            setProjectId={setProjectId}
            projects={projectsForDropdown}
            onDownload={() =>
              download('attendance/daily', { date, ...(projectId ? { project_id: projectId } : {}) })
            }
          />
        )}

        {tab === 'monthly' && (
          <MonthlyMuster
            month={month}
            setMonth={setMonth}
            projectId={projectId}
            setProjectId={setProjectId}
            projects={projectsForDropdown}
            onDownload={() =>
              download('attendance/monthly', { month, ...(projectId ? { project_id: projectId } : {}) })
            }
          />
        )}

        {tab === 'staff' && (
          <StaffMonthly
            month={month}
            setMonth={setMonth}
            onDownload={() => download('attendance/staff-monthly', { month })}
          />
        )}

        {tab === 'payouts' && (
          <Payouts
            range={range}
            setRange={setRange}
            onDownload={() => download('payments/daily', range)}
          />
        )}

        {tab === 'pay' && (
          <MonthlyPay
            month={month}
            setMonth={setMonth}
            onDownload={() => download('payments/monthly', { month })}
          />
        )}
      </div>
    </Layout>
  );
};

// --- daily ------------------------------------------------------------------

const DailyAttendance = ({ date, setDate, projectId, setProjectId, projects, onDownload }) => {
  const params = useMemo(
    () => ({ date, ...(projectId ? { project_id: projectId } : {}) }),
    [date, projectId]
  );
  const { data, isFetching } = useQuery({
    queryKey: ['report', 'attendanceDaily', params],
    queryFn: () => getDailyAttendanceReport(params)
  });

  const rows = data?.data?.rows ?? [];
  const meta = data?.meta ?? {};

  return (
    <>
      <Toolbar onDownload={onDownload} disabled={isFetching}>
        <Field label="Date">
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="rounded border border-gray-300 px-2 py-1 text-xs"
          />
        </Field>
        <ProjectField value={projectId} onChange={setProjectId} projects={projects} />
      </Toolbar>

      {/* Not marked comes first. Present and absent are decisions somebody
          made; not marked is where a day quietly goes unrecorded. */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Tile variant="card" label="Not marked" value={meta.not_marked ?? 0} tone="warning"
            icon={<HelpCircle size={18} />}
          />
        <Tile variant="card" label="Present" value={meta.present ?? 0}
            icon={<Check size={18} />}
          />
        <Tile variant="card" label="Half day" value={meta.half_day ?? 0}
            icon={<Clock size={18} />}
          />
        <Tile variant="card" label="Absent" value={meta.absent ?? 0}
            icon={<X size={18} />}
          />
        <Tile variant="card" label="Roster" value={meta.roster ?? 0}
            icon={<Users size={18} />}
          />
      </div>

      {isFetching ? (
        <Loader />
      ) : (
        <TableCard>
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <Th>Workman</Th>
              <Th>Status</Th>
              <Th>In</Th>
              <Th>Out</Th>
              <Th className="text-right">Hours</Th>
              <Th className="text-right">OT</Th>
              <Th>Site / task</Th>
              <Th>Recorded by</Th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <EmptyRow cols={8} text="Nobody on the roster for this day." />}
            {rows.map((r) => (
              <tr key={r.worker_id} className="border-t border-gray-100">
                <Td>
                  <div className="font-medium text-gray-900">{r.name}</div>
                  <div className="text-xs text-gray-500">
                    {[r.trade, r.contractor ?? 'In-house'].filter(Boolean).join(' · ')}
                  </div>
                </Td>
                <Td><AttendancePill status={r.status} declared={r.declared_status} /></Td>
                <Td className="tabular-nums">{clock(r.check_in)}</Td>
                <Td className="tabular-nums">{clock(r.check_out)}</Td>
                <Td className="text-right tabular-nums">{r.working_hours || '—'}</Td>
                <Td className="text-right tabular-nums text-warning-fg">
                  {r.overtime_hours || ''}
                </Td>
                <Td className="text-xs text-gray-600">
                  {[r.project?.name, r.task?.name].filter(Boolean).join(' · ') || '—'}
                </Td>
                <Td className="text-xs text-gray-500">{r.marked_by ?? '—'}</Td>
              </tr>
            ))}
          </tbody>
        </TableCard>
      )}
    </>
  );
};

// --- monthly muster grid ----------------------------------------------------

const MonthlyMuster = ({ month, setMonth, projectId, setProjectId, projects, onDownload }) => {
  const params = useMemo(
    () => ({ month, ...(projectId ? { project_id: projectId } : {}) }),
    [month, projectId]
  );
  const { data, isFetching } = useQuery({
    queryKey: ['report', 'attendanceMonthly', params],
    queryFn: () => getMonthlyAttendanceReport(params)
  });

  const payload = data?.data ?? {};
  const days = payload.days ?? [];
  const rows = payload.rows ?? [];
  const meta = data?.meta ?? {};

  return (
    <>
      <Toolbar onDownload={onDownload} disabled={isFetching}>
        <Field label="Month">
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="rounded border border-gray-300 px-2 py-1 text-xs"
          />
        </Field>
        <ProjectField value={projectId} onChange={setProjectId} projects={projects} />
      </Toolbar>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        <Tile variant="card" label="Workmen" value={meta.people ?? 0}
            icon={<Users size={18} />}
          />
        <Tile variant="card" label="Payable days" value={meta.payable_days ?? 0}
            icon={<CalendarDays size={18} />}
          />
        <Tile variant="card" label="Overtime hours" value={meta.overtime_hours ?? 0}
            icon={<Clock size={18} />}
          />
      </div>

      <Legend legend={payload.legend} />

      {meta.truncated && (
        <p className="rounded border border-warning-border bg-warning-bg px-3 py-2 text-xs text-warning-fg">
          Only the first {rows.length} workmen are shown. Narrow by site, or use
          the CSV download for the full list.
        </p>
      )}

      {isFetching ? (
        <Loader />
      ) : (
        <TableCard>
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <Th className="sticky left-0 z-10 bg-gray-50">Workman</Th>
              {days.map((d) => (
                <Th key={d} className="px-1 text-center font-normal">
                  {d.slice(-2)}
                </Th>
              ))}
              <Th className="text-right">Payable</Th>
              <Th className="text-right">OT</Th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <EmptyRow cols={days.length + 3} text="No attendance this month." />}
            {rows.map((r) => (
              <tr key={r.worker_id} className="border-t border-gray-100">
                <Td className="sticky left-0 z-10 bg-white">
                  <div className="whitespace-nowrap font-medium text-gray-900">{r.name}</div>
                  <div className="text-xs text-gray-500">
                    {[r.trade, r.contractor ?? 'In-house'].filter(Boolean).join(' · ')}
                  </div>
                </Td>
                {days.map((d) => (
                  <td key={d} className="px-1 py-2 text-center">
                    <MusterCell mark={r.cells[d]} />
                  </td>
                ))}
                <Td className="text-right font-medium tabular-nums">{r.payable_days}</Td>
                <Td className="text-right tabular-nums text-warning-fg">
                  {r.overtime_hours || ''}
                </Td>
              </tr>
            ))}
          </tbody>
        </TableCard>
      )}
    </>
  );
};

const StaffMonthly = ({ month, setMonth, onDownload }) => {
  const { data, isFetching } = useQuery({
    queryKey: ['report', 'staffMonthly', month],
    queryFn: () => getMonthlyStaffAttendanceReport({ month })
  });

  const payload = data?.data ?? {};
  const days = payload.days ?? [];
  const rows = payload.rows ?? [];

  return (
    <>
      <Toolbar onDownload={onDownload} disabled={isFetching}>
        <Field label="Month">
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="rounded border border-gray-300 px-2 py-1 text-xs"
          />
        </Field>
      </Toolbar>

      <Legend legend={payload.legend} />

      {isFetching ? (
        <Loader />
      ) : (
        <TableCard>
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <Th className="sticky left-0 z-10 bg-gray-50">Employee</Th>
              {days.map((d) => (
                <Th key={d} className="px-1 text-center font-normal">
                  {d.slice(-2)}
                </Th>
              ))}
              <Th className="text-right">Present</Th>
              <Th className="text-right">Leave</Th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <EmptyRow cols={days.length + 3} text="No staff attendance this month." />
            )}
            {rows.map((r) => (
              <tr key={r.user_id} className="border-t border-gray-100">
                <Td className="sticky left-0 z-10 whitespace-nowrap bg-white font-medium text-gray-900">
                  {r.name}
                </Td>
                {days.map((d) => (
                  <td key={d} className="px-1 py-2 text-center">
                    <MusterCell mark={r.cells[d]} />
                  </td>
                ))}
                <Td className="text-right font-medium tabular-nums">{r.present_days}</Td>
                <Td className="text-right tabular-nums">{r.leave_days || ''}</Td>
              </tr>
            ))}
          </tbody>
        </TableCard>
      )}
    </>
  );
};

// --- payouts ----------------------------------------------------------------

const Payouts = ({ range, setRange, onDownload }) => {
  const { data, isFetching } = useQuery({
    queryKey: ['report', 'payoutsDaily', range],
    queryFn: () => getDailyPayoutReport(range)
  });

  const payload = data?.data ?? {};
  const meta = data?.meta ?? {};

  return (
    <>
      <Toolbar onDownload={onDownload} disabled={isFetching}>
        <Field label="From">
          <input
            type="date"
            value={range.from}
            onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
            className="rounded border border-gray-300 px-2 py-1 text-xs"
          />
        </Field>
        <Field label="To">
          <input
            type="date"
            value={range.to}
            onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
            className="rounded border border-gray-300 px-2 py-1 text-xs"
          />
        </Field>
      </Toolbar>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        <Tile variant="card" label="Total paid out" value={money(meta.total_amount)}
            icon={<IndianRupee size={18} />}
          />
        <Tile variant="card" label="Payments" value={meta.payments ?? 0}
            icon={<Receipt size={18} />}
          />
        <Tile variant="card" label="Days with payments" value={meta.days ?? 0}
            icon={<CalendarRange size={18} />}
          />
      </div>

      {isFetching ? (
        <Loader />
      ) : (
        <>
          <TableCard title="By day">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <Th>Date</Th>
                <Th className="text-right">Payments</Th>
                <Th className="text-right">Amount</Th>
                <Th>By mode</Th>
              </tr>
            </thead>
            <tbody>
              {(payload.days ?? []).length === 0 && (
                <EmptyRow cols={4} text="No payments in this range." />
              )}
              {(payload.days ?? []).map((d) => (
                <tr key={d.date} className="border-t border-gray-100">
                  <Td className="whitespace-nowrap">{d.date}</Td>
                  <Td className="text-right tabular-nums">{d.count}</Td>
                  <Td className="text-right font-medium tabular-nums">{money(d.amount)}</Td>
                  <Td className="text-xs text-gray-600">
                    {Object.entries(d.modes ?? {})
                      .map(([mode, amount]) => `${titleCase(mode)} ${money(amount)}`)
                      .join(' · ')}
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableCard>

          <TableCard title="Every payment">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <Th>Date</Th>
                <Th>Workman</Th>
                <Th>Site</Th>
                <Th className="text-right">Amount</Th>
                <Th>Mode</Th>
                <Th>Reference</Th>
                <Th>Paid by</Th>
              </tr>
            </thead>
            <tbody>
              {(payload.payments ?? []).length === 0 && (
                <EmptyRow cols={7} text="No payments in this range." />
              )}
              {(payload.payments ?? []).map((p) => (
                <tr key={p.id} className="border-t border-gray-100">
                  <Td className="whitespace-nowrap">{p.paid_on}</Td>
                  <Td>{p.worker?.name ?? p.contractor ?? '—'}</Td>
                  <Td className="text-gray-600">{p.project ?? '—'}</Td>
                  <Td className="text-right tabular-nums">{money(p.amount)}</Td>
                  <Td className="text-xs">{titleCase(p.mode)}</Td>
                  <Td className="text-xs text-gray-500">{p.reference ?? '—'}</Td>
                  <Td className="text-xs text-gray-500">{p.paid_by ?? '—'}</Td>
                </tr>
              ))}
            </tbody>
          </TableCard>
        </>
      )}
    </>
  );
};

const MonthlyPay = ({ month, setMonth, onDownload }) => {
  const { data, isFetching } = useQuery({
    queryKey: ['report', 'payMonthly', month],
    queryFn: () => getMonthlyPayReport({ month })
  });

  const rows = data?.data?.rows ?? [];
  const meta = data?.meta ?? {};

  return (
    <>
      <Toolbar onDownload={onDownload} disabled={isFetching}>
        <Field label="Month">
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="rounded border border-gray-300 px-2 py-1 text-xs"
          />
        </Field>
      </Toolbar>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile variant="card" label="Earned this month" value={money(meta.total_earned)}
            icon={<TrendingUp size={18} />}
          />
        <Tile variant="card" label="Paid this month" value={money(meta.total_paid)}
            icon={<IndianRupee size={18} />}
          />
        <Tile variant="card" label="Advances" value={money(meta.total_advance)}
            icon={<HandCoins size={18} />}
          />
        <Tile variant="card" label="Outstanding (all time)" value={money(meta.total_outstanding)} tone="warning"
            icon={<Wallet size={18} />}
          />
      </div>

      {isFetching ? (
        <Loader />
      ) : (
        <TableCard>
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <Th>Workman</Th>
              <Th className="text-right">Earned</Th>
              <Th className="text-right">of which OT</Th>
              <Th className="text-right">Paid</Th>
              <Th className="text-right">Advance</Th>
              <Th className="text-right">Fines &amp; ded.</Th>
              <Th className="text-right">Outstanding</Th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <EmptyRow cols={7} text="No wage activity this month." />}
            {rows.map((r) => (
              <tr key={r.worker_id} className="border-t border-gray-100">
                <Td>
                  <div className="font-medium text-gray-900">{r.name}</div>
                  <div className="text-xs text-gray-500">
                    {[r.trade, r.contractor ?? 'In-house'].filter(Boolean).join(' · ')}
                  </div>
                </Td>
                <Td className="text-right tabular-nums">{money(r.earned)}</Td>
                <Td className="text-right tabular-nums text-gray-500">{money(r.overtime)}</Td>
                <Td className="text-right tabular-nums text-gray-500">{money(r.paid)}</Td>
                <Td className="text-right tabular-nums text-gray-500">{money(r.advance)}</Td>
                <Td className="text-right tabular-nums text-gray-500">{money(r.deductions)}</Td>
                <Td
                  className={`text-right font-medium tabular-nums ${
                    r.outstanding > 0 ? 'text-warning-fg' : 'text-gray-700'
                  }`}
                >
                  {money(r.outstanding)}
                </Td>
              </tr>
            ))}
          </tbody>
        </TableCard>
      )}

      {/* Said explicitly: a month-only balance would suggest a workman carrying
          an advance from March is owed their full April earnings. */}
      <p className="text-xs text-gray-500">
        Outstanding is the all-time balance, not this month&apos;s — it is the
        figure to settle from.
      </p>
    </>
  );
};

// --- shared bits ------------------------------------------------------------

const Toolbar = ({ children, onDownload, disabled }) => (
  <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-3">
    {children}
    <button
      type="button"
      disabled={disabled}
      onClick={onDownload}
      className="ml-auto flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 disabled:opacity-50"
    >
      <Download size={13} /> Download CSV
    </button>
  </div>
);

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-1">
    <span className="text-[10px] uppercase tracking-wide text-gray-500">{label}</span>
    {children}
  </label>
);

const ProjectField = ({ value, onChange, projects }) => (
  <Field label="Site">
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded border border-gray-300 bg-white px-2 py-1 text-xs"
    >
      <option value="">All sites</option>
      {(projects ?? []).map((p) => (
        <option key={p.id} value={p.value}>
          {p.label}
        </option>
      ))}
    </select>
  </Field>
);

/** A declared OT_ONLY day reads as a call-in, which is what it is. */
const AttendancePill = ({ status, declared }) => {
  if (!status) {
    return (
      <StatusPill dense tone="neutral">
        Not marked
      </StatusPill>
    );
  }
  const shown = declared === 'OT_ONLY' ? 'CALL_IN' : status;
  return (
    <StatusPill dense tone={toneForStatus(shown)}>
      {titleCase(shown)}
    </StatusPill>
  );
};

const TableCard = ({ title, children }) => (
  <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
    {title && (
      <div className="border-b border-gray-100 px-4 py-3 text-sm font-medium text-gray-800">
        {title}
      </div>
    )}
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">{children}</table>
    </div>
  </div>
);

const Th = ({ children, className = '' }) => (
  <th className={`whitespace-nowrap px-4 py-3 text-left font-medium ${className}`}>
    {children}
  </th>
);
const Td = ({ children, className = '' }) => (
  <td className={`px-4 py-3 align-top ${className}`}>{children}</td>
);
const EmptyRow = ({ cols, text }) => (
  <tr>
    <td colSpan={cols} className="py-10 text-center text-gray-500">
      {text}
    </td>
  </tr>
);

const MARKS = {
  P: { label: 'P', className: 'bg-success-bg text-success-fg' },
  H: { label: 'H', className: 'bg-warning-bg text-warning-fg' },
  A: { label: 'A', className: 'bg-danger-bg text-danger-fg' },
  O: { label: 'O', className: 'bg-info-bg text-info-fg' },
  L: { label: 'L', className: 'bg-info-bg text-info-fg' },
  R: { label: 'R', className: 'bg-brand-100 text-brand-800' },
  F: { label: 'F', className: 'bg-gray-200 text-gray-600' },
  W: { label: 'W', className: 'bg-gray-100 text-gray-400' },
  '-': { label: '-', className: 'bg-gray-100 text-gray-400' }
};

/**
 * A blank cell is "not marked", and it is left visibly blank rather than given
 * a symbol — a missing day should look missing, because on a muster it is the
 * thing worth noticing.
 */
const MusterCell = ({ mark }) => {
  if (!mark) {
    return <span className="inline-block h-6 w-6 rounded border border-dashed border-gray-300" />;
  }
  const style = MARKS[mark] ?? MARKS['-'];
  return (
    <span
      className={`inline-flex h-6 w-6 items-center justify-center rounded text-[11px] font-semibold ${style.className}`}
    >
      {style.label}
    </span>
  );
};

const Legend = ({ legend }) => {
  if (!legend) return null;
  return (
    <div className="flex flex-wrap gap-3 rounded-lg border border-gray-200 bg-white px-4 py-2 text-xs text-gray-600">
      {Object.entries(legend).map(([mark, meaning]) => (
        <span key={meaning} className="flex items-center gap-1.5">
          <MusterCell mark={mark} />
          {meaning}
        </span>
      ))}
    </div>
  );
};

function todayString() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
}

function thisMonthString() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function clock(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function money(value) {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return '₹0';
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function titleCase(value) {
  if (!value) return '—';
  return value
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export default WorkforceReports;
